import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment/moment';
import { useLocation, useHistory } from 'react-router-dom';
import { useAccountMetrics } from 'new/hooks/adapters/useAccountMetrics';
import { fetchOrgBillableEvents, fetchUserBillableEvents } from 'js/actions';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ViewType } from '../constants/viewTypes';
import {
  parseAccountUsageDateRange,
  defaultAccountUsageRange,
  buildAccountUsageQuery,
} from './dateRangeFromQuery';

export const useAccountMetricsView = () => {
  const location = useLocation();
  const history = useHistory();
  const initialRange = useMemo(
    () => parseAccountUsageDateRange(location.search) ?? defaultAccountUsageRange(),
    [location.search],
  );
  // inclusive day count: a 7d preset reads "7 days", matching the quick-filter label
  const initialDaysDiff =
    moment(initialRange.endDate).diff(moment(initialRange.startDate), 'days') + 1;

  const org = useCurrentOrg();
  const user = useCurrentUser();
  const dispatch = useAppDispatch();
  const { userData, orgData, isLoading } = useAccountMetrics();

  const [isOrgView, setIsOrgView] = useState(user.isAdmin ? true : false);
  const [daysDiff, setDaysDiff] = useState(initialDaysDiff);
  const [dates, setDates] = useState(initialRange);

  const metrics = isOrgView ? orgData : userData;
  const viewType = isOrgView ? ViewType.Org : ViewType.Mine;

  const onDatesChanged = useCallback(
    (startDate: Date, endDate: Date) => {
      setDates({ startDate, endDate });
      try {
        // inclusive day count: a 7d preset reads "7 days", matching the quick-filter label
        const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
        setDaysDiff(daysDiff);
      } catch (error) {
        console.error(error);
      }
      const search = buildAccountUsageQuery({ startDate, endDate });
      history.replace(`${history.location.pathname}?${search}${history.location.hash}`);
    },
    [history],
  );

  const onControlChange = useCallback(() => setIsOrgView((prev) => !prev), []);

  /**
   * Fetches account usage metrics whenever the view (org/personal), date range, or user changes.
   * - If viewing as org and user is admin, fetches organization billable events.
   * - Otherwise, fetches user billable events for the current org and user.
   */
  useEffect(() => {
    // Account metrics are bucketed by UTC day; pass UTC day-strings so the
    // summary range matches the UTC range shown in the controls/trend charts.
    const from = moment.utc(dates.startDate).format('YYYY-MM-DD');
    const to = moment.utc(dates.endDate).format('YYYY-MM-DD');
    if (isOrgView && user.isAdmin) {
      dispatch(
        fetchOrgBillableEvents({
          orgId: org.id,
          startDate: from,
          endDate: to,
        }),
      );
    } else {
      dispatch(
        fetchUserBillableEvents({
          userId: user.id,
          startDate: from,
          endDate: to,
          orgId: org.id,
        }),
      );
    }
  }, [isOrgView, dates, user]);

  return {
    metrics,
    viewType,
    daysDiff,
    dates,
    isOrgView,
    onDatesChanged,
    onControlChange,
    isAdmin: user.isAdmin,
    isLoading,
  };
};
